import {
  useGetOrderRatingNeighborsQuery,
  useGetOrderRatingQuery,
} from 'api/orderRatings'
import type { OrderRatingDetail } from 'api/orderRatings.types'
import type { FeedbackTableRow } from 'components/FeedbackTable'
import { FieldGridItem } from 'components/FieldGrid'
import { buildTranslate } from 'locales'
import { useMemo } from 'react'
import { useAbility } from 'utilities/ability'
import type { RatingDetailPager, RatingDetailView } from './ratingDetailView'
import { formatDateTime, formatTime } from './ratingFormatters'
import { resolutionStatusFor } from './resolutionStatus'
import { synthesizeTimeline } from './synthesizeTimeline'
import { RATING_DETAIL_PATH } from './useOrderRatings'
import { useRatingCustomerPanels } from './useRatingCustomerPanels'
import { useRatingDetailActions } from './useRatingDetailActions'

// The view contract lives in a leaf types module so the composer, its two
// sub-hooks, and the presentational header can all depend on it without
// importing one another. Re-exported here so existing consumers that import the
// types from `./useRatingDetail` (RatingDetailHeader et al) keep working.
export type {
  RatingDetailActions,
  RatingDetailModals,
  RatingDetailPager,
  RatingDetailView,
} from './ratingDetailView'

const t = buildTranslate('order_ratings.rating_detail')

// The Details tab this page is reached from. Also where every guard sends a
// viewer who shouldn't be here, and where the back link returns.
export const FEEDBACK_DETAILS_PATH = '/order_feedback#details'

type UseRatingDetailArgs = {
  ratingId: string
  stateRow: FeedbackTableRow | undefined
  navigate: (path: string) => void
}

// Controller hook for the order-feedback ratings-detail scene: a thin composer
// over two focused sub-hooks. It owns the ability read, the rating/neighbors
// queries, and the display derivation; useRatingCustomerPanels owns the
// customer-scoped queries and their panels; useRatingDetailActions owns the
// modal state, the mutations, and the operator handlers. Returns a
// discriminated view the scene switches on — the guards (redirect/loading/
// error/not-found) and the full ready payload — so the scene itself is pure
// composition.
export const useRatingDetail = ({
  ratingId,
  stateRow,
  navigate,
}: UseRatingDetailArgs): RatingDetailView => {
  const ability = useAbility()
  // access Feedback is this page's sole gate now that OrderFeedbackReport is
  // unconditional. Stays in the composer: it gates the rating query's `skip`
  // below, ahead of the sub-hooks and the guards.
  const canViewDetails = ability.can('access', 'Feedback')
  // The write-level Feedback action (manager / customer_service_write / admin
  // grant it; customer_service_read gets `access` only). Unlike the RAI case
  // detail — which HIDES its action row from a non-manager — product wants the
  // controls visible-but-disabled here, so a read-only viewer can see the
  // actions exist and know to ask for access.
  const canManageFeedback = ability.can('manage', 'Feedback')
  // Grant reward is a second, independently-assignable write on top of
  // canManageFeedback above — a Feedback manager without the Reward Issuance
  // permission can resolve/dismiss but can't dispense a reward.
  const canManageRewardIssuance = ability.can('manage', 'RewardIssuance')

  // currentData (not data): keyed to the current arg, so navigating between
  // ratings never paints the previous one's record. Skipped when either guard
  // below would redirect — the hook still runs (rules of hooks), but the
  // request itself never fires for a viewer who can't see this page.
  const { currentData, isFetching, isUninitialized, isError, refetch } =
    useGetOrderRatingQuery(ratingId, { skip: !canViewDetails })

  // The header "Case N of M unresolved" pager (AI-1023). Keyed on the route
  // param so it re-resolves per rating; skipped behind the same access guard.
  // `position` is null when the rating isn't in the unresolved set (resolved),
  // which hides the pager.
  const { data: neighbors } = useGetOrderRatingNeighborsQuery(ratingId, {
    skip: !canViewDetails,
  })

  // The row handed over in router state paints instantly; the query supersedes
  // it as soon as it lands (and is the only source on a deep link / refresh).
  // Built here rather than merged field-by-field so a settled response is never
  // mixed with a stale row.
  const fromState: OrderRatingDetail | null = stateRow
    ? {
        id: stateRow.id,
        rating: stateRow.rating,
        date: stateRow.date,
        customerName: stateRow.customerName,
        storeName: stateRow.locationName,
        resolvedBy: stateRow.resolvedBy,
        conversationId: stateRow.conversationId,
        comment: stateRow.comment,
        userId: stateRow.userId,
        userUid: stateRow.userUid,
        // Router state carries no order snapshot — only the query can supply it.
        order: null,
      }
    : null
  const rating = currentData ?? fromState

  const detailItems: FieldGridItem[] = useMemo(
    () =>
      rating
        ? [
            {
              label: t('details.date_time'),
              value: formatDateTime(rating.date),
            },
            {
              label: t('details.location'),
              value: rating.storeName ?? t('empty'),
            },
          ]
        : [],
    [rating]
  )

  // The Case timeline card's 2-step stepper — synthesized client-side (no
  // backend timeline field on an OrderRating).
  const timelineItems = useMemo(
    () => (rating ? synthesizeTimeline(rating, formatTime, t) : []),
    [rating]
  )

  // Both sub-hooks are called UNCONDITIONALLY here, ahead of every early-return
  // guard below (rules of hooks). Each tolerates a null rating — its queries
  // skip and its derived values fall back — so a viewer about to be redirected,
  // or a cold load with no row yet, is safe.
  const panels = useRatingCustomerPanels(rating, ratingId)
  const actions = useRatingDetailActions(rating, ratingId, {
    canManageFeedback,
    canManageRewardIssuance,
    canViewDetails,
  })

  if (!canViewDetails) {
    return { status: 'redirect', to: FEEDBACK_DETAILS_PATH }
  }

  // A conversation-backed rating belongs to the real case detail, which owns the
  // transcript and the operator actions. Only reachable by hand-typed URL — the
  // table routes those rows straight there.
  const conversationId = rating?.conversationId
  if (conversationId) {
    return { status: 'redirect', to: `/recovery_ai/cases/${conversationId}` }
  }

  // Only a cold load blocks on the query: with a row in router state the page is
  // already painted and the fetch is pure enrichment (the order snapshot).
  if (!rating) {
    if (isUninitialized || isFetching) return { status: 'loading' }
    if (isError) return { status: 'error', onRetry: refetch }
    return { status: 'notFound' }
  }

  const order = currentData?.order ?? null
  // With a router-state row, `rating` is already truthy while the enrichment
  // fetch is still resolving, so a failed GET has to be told apart here from a
  // rating that settled with no order — the latter is a quiet empty state,
  // the former an unverified fact and needs its own error + retry.
  const orderFetchSettled = !isFetching && !isUninitialized
  const showOrderError = isError && orderFetchSettled
  const showOrderUnavailable = !isError && !order && orderFetchSettled

  // The header title: the human order number once the (enrichment-fetch-only)
  // order snapshot has one, else the customer's name (OQ4). DATA-4857 means
  // orderNumber is producer-nil today, so this reads as customerName in
  // practice until the backend backfills it — expected, not a bug.
  const title = order?.orderNumber
    ? t('order_heading', { number: order.orderNumber })
    : rating.customerName ?? t('empty')

  // On the warm path (a row handed over in router state) the page is painted
  // before the order snapshot lands, so `title` above would resolve to the
  // customerName fallback and then swap to "Order #…" a beat later — the name
  // visibly flashed into the header. Show a placeholder until the fallback is a
  // real answer rather than a not-yet-known one.
  //
  // Deliberately NOT `!orderFetchSettled` (the signal the order card uses):
  // isFetching is also true on a REFETCH, when currentData is still populated —
  // and grant / mark-resolved / send-message all invalidate this rating's own
  // tag, so that spun the already-known title back into a spinner on every
  // operator action. The question here is "is the snapshot known yet", not "is a
  // request in flight": only `currentData` can carry an order number, so the
  // title is unknowable exactly while there is no currentData AND something is
  // still on its way to supply it. isUninitialized covers the tick between
  // mount and the request firing, when nothing is in flight but nothing has
  // arrived either. Never true on a cold load — no rating means status
  // 'loading' for the whole page, above.
  const isTitleLoading = !currentData && (isFetching || isUninitialized)

  // A rating nobody has closed yet is the only one the operator can act on —
  // `resolvedBy.kind` is this surface's resolution signal (avoid a double-resolve).
  const isRatingResolved = !!rating.resolvedBy.kind

  // The pager shows only when this rating is in the unresolved set (position
  // non-null); prev/next route to the neighbor rating's own detail page.
  const goToNeighbor = (id: string | null) => () => {
    if (id) navigate(`${RATING_DETAIL_PATH}/${id}`)
  }

  const pager: RatingDetailPager | null =
    neighbors && neighbors.position != null
      ? {
          position: neighbors.position,
          total: neighbors.total,
          hasPrev: neighbors.previousId != null,
          hasNext: neighbors.nextId != null,
          onPrev: goToNeighbor(neighbors.previousId),
          onNext: goToNeighbor(neighbors.nextId),
        }
      : null

  return {
    status: 'ready',
    badgeStatus: resolutionStatusFor(rating.resolvedBy.kind),
    title,
    isTitleLoading,
    subtitle: formatDateTime(rating.date),
    showActions: !isRatingResolved,
    pager,
    actions: actions.headerActions,
    comment: rating.comment,
    detailItems,
    order,
    showOrderError,
    showOrderUnavailable,
    onRetryOrder: refetch,
    timelineItems,
    customerInfo: panels.customerInfo,
    profileUrl: panels.profileUrl,
    recentRatingRows: panels.recentRatingRows,
    modals: {
      canManageFeedback,
      canManageRewardIssuance,
      isRatingResolved,
      customerName: rating.customerName,
      customerEmailState: panels.customerEmailState,
      isResolving: actions.isResolving,
      isResolveError: actions.isResolveError,
      resetResolve: actions.resetResolve,
      isGrantModalOpen: actions.isGrantModalOpen,
      onCloseGrant: actions.onCloseGrant,
      availableRewards: actions.availableRewards,
      isFetchingRewards: actions.isFetchingRewards,
      isRewardsError: actions.isRewardsError,
      showPointsOption: actions.showPointsOption,
      onGrantConfirm: actions.onGrantConfirm,
      isResolveModalOpen: actions.isResolveModalOpen,
      onCloseResolve: actions.onCloseResolve,
      operatorEmail: actions.operatorEmail,
      onResolveConfirm: actions.onResolveConfirm,
      isSendMessageModalOpen: actions.isSendMessageModalOpen,
      onCloseSendMessage: actions.onCloseSendMessage,
      onSendMessage: actions.onSendMessage,
      isSendingMessage: actions.isSendingMessage,
      isSendMessageError: actions.isSendMessageError,
      resetSendMessage: actions.resetSendMessage,
      sendMessageErrorMessage: actions.sendMessageErrorMessage,
    },
  }
}
